<script setup>
import { computed } from 'vue';
const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
});

const getFormattedPages = (start, end) => {
  const formatter = new Intl.NumberFormat(navigator.language);
  return Array.from({ length: end - start + 1 }, (_, i) =>
    formatter.format(start + i)
  );
};

const currentPage = computed(() => {
  return props.table.getState().pagination.pageIndex + 1;
});

const totalPages = computed(() => {
  return props.table.getPageCount();
});

const visiblePages = computed(() => {
  const current = currentPage.value;
  const total = totalPages.value;
  
  if (total <= 7) {
    return getFormattedPages(1, total);
  }
  
  if (current <= 4) {
    return [...getFormattedPages(1, 5), '...', ...getFormattedPages(total, total)];
  }
  
  if (current >= total - 3) {
    return [1, '...', ...getFormattedPages(total - 4, total)];
  }
  
  return [1, '...', ...getFormattedPages(current - 1, current + 1), '...', ...getFormattedPages(total, total)];
});

const isCurrentPage = (page) => {
  if (page === '...') return false;
  
  const pageNumber = parseInt(page.replace(/,/g, ''), 10);
  return pageNumber === currentPage.value;
};

const total = computed(() => {
  return props.table.getRowCount();
});

const canGoPrevious = computed(() => {
  return props.table.getState().pagination.pageIndex > 0;
});

const canGoNext = computed(() => {
  const { pagination } = props.table.getState();
  const totalRows = props.table.getRowCount();
  return (pagination.pageIndex + 1) * pagination.pageSize < totalRows;
});

const handlePageClick = (page) => {
  if (typeof page === 'number') {
    props.table.setPageIndex(page - 1);
  }
};
</script>

<template>
  <div class="flex items-center justify-between w-full py-3">
    <button
      :disabled="!canGoPrevious"
      class="flex items-center gap-2 px-4 py-[10px] text-sm font-semibold text-slate-600 dark:text-n-gray-5 disabled:cursor-not-allowed hover:text-slate-800 dark:hover:text-n-gray-7 dark:hover:bg-n-vars-black-br/30 rounded-l-lg rounded-r-none"
      @click="table.previousPage()"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
      {{ $t('GENERAL.PAGINATION.PREVIOUS') }}
    </button>

    <div class="flex items-center gap-1">
      <template v-for="(page, index) in visiblePages" :key="index">
        <span
          v-if="page === '...'"
          class="w-8 h-8 flex items-center justify-center text-sm font-medium text-n-gray-7"
        >
          {{ page }}
        </span>
        <button
          v-else
          :class="[
            'w-8 h-8 flex items-center justify-center text-sm font-medium rounded transition-colors',
            isCurrentPage(page)
              ? '!text-n-gray-5 dark:!text-white'
              : 'text-n-gray-3 dark:text-n-gray-7 hover:text-n-gray-5 hover:bg-gray-100 dark:hover:bg-gray-800'
          ]"
          @click="handlePageClick(parseInt(page.replace(/,/g, ''), 10))"
        >
          {{ page }}
        </button>
      </template>
    </div>

    <button
      :disabled="!canGoNext"
      class="flex items-center gap-2 px-4 py-[10px] text-sm font-semibold text-slate-600 dark:text-n-gray-5 disabled:cursor-not-allowed hover:text-slate-800 dark:hover:text-n-gray-7 dark:hover:bg-n-vars-black-br/30 rounded-r-lg rounded-l-none"
      @click="table.nextPage()"
    >
      {{ $t('GENERAL.PAGINATION.NEXT') }}
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
:deep(.cw-contacts-table thead) {
@apply !bg-n-vars-black-bd/5;
}
</style>